import React, {Fragment, useMemo, useState} from "react";
import {
    Dialog,
    DialogPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
    TransitionChild
} from "@headlessui/react";
import {
    BanknotesIcon,
    Bars3Icon,
    BuildingOffice2Icon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    CreditCardIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    LinkIcon,
    MegaphoneIcon,
    PuzzlePieceIcon,
    RectangleStackIcon,
    ShieldExclamationIcon,
    Squares2X2Icon,
    CalendarDaysIcon,
    TableCellsIcon,
    UserGroupIcon,
    UserPlusIcon,
    WrenchScrewdriverIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {Link, Navigate, NavLink, Outlet, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../context/AuthContext";
import {useEmailFailureCounts} from "../hooks/useEmailFailures";

import logo from "../assets/logo_alt.png";
import {ChevronDownIcon} from "@heroicons/react/16/solid";

type LeafNavItem = {
    type?: "link";
    name: string;
    to: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    end?: boolean;
    /** Something waiting on an administrator, shown beside the link while above zero. */
    badge?: number;
};

type GroupNavItem = {
    type: "group";
    name: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    children: LeafNavItem[];
};

type NavItem = LeafNavItem | GroupNavItem;

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const navLinkClass = (isActive: boolean, collapsed = false) =>
    cx(
        isActive ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white",
        "group flex rounded-md p-2 text-sm/6 font-semibold",
        collapsed ? "justify-center" : "gap-x-3"
    );

function isGroup(item: NavItem): item is GroupNavItem {
    return item.type === "group";
}

export default function AdminLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const {user, logout, isReady: authReady} = useAuth();
    // Polled here because the sidebar is the one place every administrator passes. A failed
    // ticket email gets noticed there rather than found.
    const {data: emailFailureCounts} = useEmailFailureCounts();
    const outstandingEmailFailures = emailFailureCounts?.outstanding ?? 0;

    const navigation: NavItem[] = useMemo(
        () => [
            {name: "Dashboard", to: "/admin/dashboard", icon: Squares2X2Icon, end: true},
            {name: "Businesses", to: "/admin/businesses", icon: BuildingOffice2Icon, end: true},
            {name: "Operations", to: "/admin/operations", icon: CalendarDaysIcon, end: true},
            {
                type: "group",
                name: "Finance",
                icon: CurrencyDollarIcon,
                children: [
                    {name: "Payout Requests", to: "/admin/payouts", icon: BanknotesIcon, end: true},
                    {name: "Grouped Payouts", to: "/admin/payout-groups", icon: RectangleStackIcon, end: false},
                    {name: "Card Recovery", to: "/admin/card-recovery", icon: ShieldExclamationIcon, end: true},
                    {name: "Failed Payments", to: "/admin/failed-payments", icon: ExclamationTriangleIcon, end: true},
                    {name: "Card Payment Links", to: "/admin/card-pay-links", icon: LinkIcon, end: true},
                    {name: "Sales Agents", to: "/admin/sales-agents", icon: UserGroupIcon, end: true},
                ],
            },
            {name: "Employee Accounts", to: "/admin/employee-accounts", icon: UserPlusIcon, end: true},
            {name: "Cashless", to: "/admin/cashless", icon: CreditCardIcon, end: true},
            {name: "Top-up Exports", to: "/admin/cashless/topup-exports", icon: DocumentTextIcon, end: true},
            {name: "Event Discovery", to: "/admin/discovery", icon: GlobeAltIcon, end: true},
            {name: "Partners", to: "/admin/partners", icon: PuzzlePieceIcon, end: true},
            {name: "Banners", to: "/admin/banners", icon: MegaphoneIcon, end: true},
            {name: "Newsletter", to: "/admin/newsletter", icon: EnvelopeIcon, end: true},
            {
                name: "Failed Emails",
                to: "/admin/email-failures",
                icon: ExclamationCircleIcon,
                end: true,
                badge: outstandingEmailFailures,
            },
            {name: "Ticket Corrections", to: "/admin/ticket-corrections", icon: WrenchScrewdriverIcon, end: true},
            {
                type: "group",
                name: "Seating",
                icon: TableCellsIcon,
                children: [
                    {name: "Seat Maps", to: "/admin/seatmaps", icon: TableCellsIcon, end: true},
                    {name: "Duplicate Seats", to: "/admin/seating", icon: ExclamationTriangleIcon, end: false},
                ],
            },
        ],
        [outstandingEmailFailures]
    );

    // Desktop collapse (icon-only rail) + controlled group expansion.
    const [collapsed, setCollapsed] = useState(false);
    const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
        const s = new Set<string>();
        navigation.forEach((item) => {
            if (isGroup(item) && item.children.some((c) => location.pathname.startsWith(c.to))) {
                s.add(item.name);
            }
        });
        return s;
    });
    const toggleGroup = (name: string) =>
        setOpenGroups((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });

    if (!authReady) {
        return <div className="p-6">Loading…</div>;
    }

    if (!user) {
        return <Navigate to="/" replace state={{from: location.pathname}}/>;
    }

    const handleLogout = () => {
        logout();
        navigate("/", {replace: true});
    };

    const displayName = user?.name ?? user?.email ?? "Account";

    const SidebarNav = ({onNavigate, collapsed = false}: { onNavigate?: () => void; collapsed?: boolean }) => (
        <nav className="mt-2 flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-2">
                {navigation.map((item) => {
                    if (!isGroup(item)) {
                        return (
                            <li key={item.name}>
                                <NavLink
                                    to={item.to}
                                    end={item.end}
                                    onClick={onNavigate}
                                    title={collapsed ? item.name : undefined}
                                    className={({isActive}) => navLinkClass(isActive, collapsed)}
                                >
                                    <span className="relative shrink-0">
                                        <item.icon aria-hidden="true" className="size-6"/>
                                        {collapsed && !!item.badge && (
                                            <span
                                                aria-hidden="true"
                                                className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-red-500 ring-2 ring-gray-900"
                                            />
                                        )}
                                    </span>
                                    {!collapsed && <span className="flex-1">{item.name}</span>}
                                    {!collapsed && !!item.badge && (
                                        <span
                                            aria-hidden="true"
                                            className="ml-auto rounded-full bg-red-500 px-2 text-xs/5 font-semibold text-white"
                                        >
                                            {item.badge > 99 ? "99+" : item.badge}
                                        </span>
                                    )}
                                    {!!item.badge && <span className="sr-only">, {item.badge} outstanding</span>}
                                </NavLink>
                            </li>
                        );
                    }

                    // Collapsed: icon-only button that re-expands the sidebar and opens this group.
                    if (collapsed) {
                        return (
                            <li key={item.name}>
                                <button
                                    type="button"
                                    title={item.name}
                                    onClick={() => {
                                        setCollapsed(false);
                                        setOpenGroups((prev) => new Set(prev).add(item.name));
                                    }}
                                    className="group flex w-full justify-center rounded-md p-2 text-sm/6 font-semibold text-gray-300 hover:bg-white/5 hover:text-white"
                                >
                                    <item.icon aria-hidden="true" className="size-6 shrink-0"/>
                                </button>
                            </li>
                        );
                    }

                    // Expanded: controlled group / dropdown in sidebar.
                    const open = openGroups.has(item.name);
                    return (
                        <li key={item.name}>
                            <button
                                type="button"
                                onClick={() => toggleGroup(item.name)}
                                className={cx(
                                    "w-full",
                                    "group flex items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                                    open ? "bg-white/5 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
                                )}
                            >
                                <item.icon aria-hidden="true" className="size-6 shrink-0"/>
                                <span className="flex-1 text-left">{item.name}</span>
                                <ChevronDownIcon
                                    className={cx("size-5 text-gray-400 transition", open && "rotate-180")}
                                    aria-hidden="true"
                                />
                            </button>

                            {open && (
                                <ul className="mt-1 space-y-1 pl-2">
                                    {item.children.map((child) => (
                                        <li key={child.name}>
                                            <NavLink
                                                to={child.to}
                                                end={child.end}
                                                onClick={onNavigate}
                                                className={({isActive}) =>
                                                    cx(
                                                        isActive
                                                            ? "bg-white/10 text-white"
                                                            : "text-gray-300 hover:bg-white/5 hover:text-white",
                                                        "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                                                    )
                                                }
                                            >
                                                <child.icon aria-hidden="true"
                                                            className="size-5 shrink-0 opacity-90"/>
                                                {child.name}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    return (
        <div className="min-h-screen">
            {/* Mobile sidebar */}
            <Transition show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    <TransitionChild
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80"/>
                    </TransitionChild>

                    <div className="fixed inset-0 flex">
                        <TransitionChild
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <div className="absolute top-0 left-full flex w-16 justify-center pt-5">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(false)}
                                        className="-m-2.5 p-2.5 text-white hover:bg-white/10 rounded-md"
                                    >
                                        <span className="sr-only">Close sidebar</span>
                                        <XMarkIcon aria-hidden="true" className="size-6"/>
                                    </button>
                                </div>

                                <div
                                    className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                                    <div className="flex h-16 shrink-0 items-center">
                                        <Link to="/" className="m-1.5" onClick={() => setSidebarOpen(false)}>
                                            <img src={logo} alt="NamEvents" className="mt-8 h-16 w-auto invert"/>
                                        </Link>
                                    </div>

                                    <SidebarNav onNavigate={() => setSidebarOpen(false)}/>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>

            {/* Desktop sidebar */}
            <div
                className={cx(
                    "hidden bg-gray-900 ring-1 ring-white/10 transition-[width] duration-300 lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col",
                    collapsed ? "lg:w-20" : "lg:w-72"
                )}>
                <div className={cx("flex grow flex-col gap-y-5 overflow-y-auto bg-black/10 pb-4", collapsed ? "px-3" : "px-6")}>
                    <div className="flex h-16 shrink-0 items-center">
                        {!collapsed && (
                            <Link to="/" className="m-1.5" aria-label="NamEvents">
                                <img src={logo} alt="NamEvents" className="mt-8 h-16 w-auto invert" loading="eager"
                                     decoding="async"/>
                                <span className="sr-only">NamEvents</span>
                            </Link>
                        )}
                    </div>

                    <SidebarNav collapsed={collapsed}/>
                </div>
            </div>

            {/* Main column */}
            <div className={cx("transition-[padding] duration-300", collapsed ? "lg:pl-20" : "lg:pl-72")}>
                {/* Top bar */}
                <div
                    className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900 lg:hidden"
                    >
                        <span className="sr-only">Open sidebar</span>
                        <Bars3Icon aria-hidden="true" className="size-6"/>
                    </button>

                    <button
                        type="button"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-expanded={!collapsed}
                        className="-m-2.5 hidden p-2.5 text-gray-700 hover:text-gray-900 lg:inline-flex"
                    >
                        {collapsed
                            ? <ChevronDoubleRightIcon aria-hidden="true" className="size-6"/>
                            : <ChevronDoubleLeftIcon aria-hidden="true" className="size-6"/>}
                    </button>

                    <div aria-hidden="true" className="h-6 w-px bg-gray-900/10 lg:hidden"/>

                    <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">

                        <div className="grid flex-1 items-center grid-cols-1"></div>

                        <div className="flex items-center gap-x-4 lg:gap-x-6">

                            {/*<button type="button" className="-m-2.5 p-2.5 text-gray-500 hover:text-gray-500">*/}
                            {/*    <span className="sr-only">View notifications</span>*/}
                            {/*    <BellIcon aria-hidden="true" className="size-6"/>*/}
                            {/*</button>*/}

                            {/*<div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"/>*/}

                            <Menu as="div" className="relative">
                                <MenuButton className="relative flex items-center rounded-full focus:outline-none">
                                    <span className="sr-only">Open user menu</span>
                                    <img
                                        alt=""
                                        src={
                                            user?.avatarUrl ||
                                            `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=0D8ABC&color=fff`
                                        }
                                        className="size-8 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5"
                                    />
                                    <span className="hidden lg:flex lg:items-center">
                                        <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
                                          {displayName}
                                        </span>
                                        <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400"/>
                                    </span>
                                </MenuButton>

                                <MenuItems
                                    className="absolute right-0 z-10 mt-2.5 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg outline outline-gray-900/5">
                                    <MenuItem>
                                        {({focus}) => (
                                            <Link
                                                to="/profile"
                                                className={cx("block px-3 py-1 text-sm/6 text-gray-900", focus && "bg-gray-50")}
                                            >
                                                Your profile
                                            </Link>
                                        )}
                                    </MenuItem>

                                    <MenuItem>
                                        {({focus}) => (
                                            <button
                                                type="button"
                                                onClick={handleLogout}
                                                className={cx(
                                                    "block w-full px-3 py-1 text-left text-sm/6 text-gray-900",
                                                    focus && "bg-gray-50"
                                                )}
                                            >
                                                Sign out
                                            </button>
                                        )}
                                    </MenuItem>
                                </MenuItems>
                            </Menu>
                        </div>
                    </div>
                </div>

                <main className="py-10">
                    <div className="px-4 sm:px-6 lg:px-8">
                        <Outlet/>
                    </div>
                </main>
            </div>
        </div>
    );
}